import React, { useState, useEffect, lazy, Suspense } from 'react'
import { Route, Navigate, Outlet, useOutletContext, useLocation } from 'react-router';
import {
	publicRoutes,
	privateRoutes,
	adminPrivateRoutes,
	deliveryPrivateRoutes,
	partnerPrivateRoutes,
} from './routes'

import Layout from 'containers/layout/owner/Layout';
import LayoutAdmin from 'containers/layout/admin/Layout';
import LayoutDelivery from 'containers/layout/delivery/Layout';
import LayoutPartner from 'containers/layout/partner/Layout';
import NoAuthLayout from 'containers/layout/NoAuthLayout';
import { adminMenuItems, adminPermissionByPath } from 'containers/layout/admin/menu';
import { hasPermission } from 'helpers/permissions';
import { loadedNavigationAccess, loadNavigationAccess, isPathAllowed, allowedFallbackPath } from 'helpers/navigationAccess';

// Отдельный чанк: гость открывает страницу с телефона, весь кабинет ему не нужен
const AccountDeletion = lazy(() => import('pages/public/AccountDeletion'));

/**
 * Первый раздел админки, доступный этому сотруднику.
 *
 * Раньше домашней страницей админа был жёсткий `/admin/poses` — единственный админ
 * видел всё. Теперь у продажника «Заведений» может не быть вовсе, и после входа его
 * встретил бы редирект в запрещённый раздел.
 */
function firstAdminPath(account) {
	// Привычная стартовая страница админки — список заведений. Отдаём её всем, у кого
	// есть доступ, и только остальным подбираем первый доступный раздел.
	if (hasPermission('admin_poses', account)) {
		return '/admin/poses'
	}

	const items = adminMenuItems()

	for (let i = 0; i < items.length; i++) {
		if (hasPermission(items[i].permission, account)) {
			return items[i].url
		}
	}

	return null
}

export function getDefaultHomePath(role, account) {
	switch (role) {
		case 'ROLE_ADMIN':
			return firstAdminPath(account) || '/auth/login'
		case 'ROLE_OWNER':
		case 'ROLE_DIRECTOR':
			return '/analytics/dashboard'
		case 'ROLE_CASHIER':
			return '/hello'
		case 'ROLE_CALL_CENTER':
			return '/delivery/orders'
		case 'ROLE_PARTNER':
			return '/partner/poses'
		default:
			return '/auth/login'
	}
}

function GuestOnlyRoute() {
	const login = localStorage.getItem('login')
	const role = localStorage.getItem('role')

	if (login) {
		return <Navigate to={getDefaultHomePath(role)} replace />
	}

	return <Outlet />
}

function createPublicRoutes() {
	return publicRoutes.map((item, key) => {
		return (
			<Route key={key} path={item.path} element={item.component} />
		);
	});
}

function createPrivateRoutes() {
	return privateRoutes.map((item, key) => {
		return (
			<Route key={key} element={<ProtectedOwnerRoute />}>
				<Route path={item.path} element={item.component} />
			</Route>
		);
	});
}

function createAdminPrivateRoutes() {
	return adminPrivateRoutes.map((item, key) => {
		return (
			<Route key={key} element={<ProtectedAdminRoute />}>
				<Route path={item.path} element={item.component} />
			</Route>
		);
	});
}

function createDeliveryPrivateRoutes() {
	return deliveryPrivateRoutes.map((item, key) => {
		return (
			<Route key={key} element={<ProtectedDeliveryRoute />}>
				<Route path={item.path} element={item.component} />
			</Route>
		);
	});
}

function createPartnerPrivateRoutes() {
	return partnerPrivateRoutes.map((item, key) => {
		return (
			<Route key={key} element={<ProtectedRoute />}>
				<Route path={item.path} element={item.component} />
			</Route>
		);
	});
}

function AuthenticatedFallbackRedirect() {
	const role = localStorage.getItem('role')
	return <Navigate to={getDefaultHomePath(role)} replace />
}

// Корень админки: у каждого сотрудника свой первый доступный раздел.
function AdminIndexRedirect() {
	return <Navigate to={firstAdminPath() || '/auth/login'} replace />
}

function ProtectedRoute() {
	const login = localStorage.getItem("login");
	const outletContextFromParent = useOutletContext();
	var isAuthenticated = false;
	if (login) {
		isAuthenticated = true
	} else {
		isAuthenticated = false
	}

	if (!isAuthenticated) {
		localStorage.removeItem('login')
		localStorage.removeItem('role')
		localStorage.removeItem('token')
		return <Navigate to="/auth/login" />;
	}

	return <Outlet context={outletContextFromParent} />;
};

/**
 * Кабинет заведения: кроме входа сверяем текущий путь с доступом к пунктам меню.
 * Раньше проверялся только логин, и страницу без права открывали прямой ссылкой
 * или оставались на ней после переключения на связанный аккаунт. Владельцу открыто
 * всё — для него запрос не делаем.
 */
function ProtectedOwnerRoute() {
	const login = localStorage.getItem('login')
	const role = localStorage.getItem('role')
	const location = useLocation()
	const outletContextFromParent = useOutletContext()
	const checkAccess = !!login && role !== 'ROLE_OWNER'
	const [access, setAccess] = useState(() => checkAccess ? loadedNavigationAccess() : null)

	useEffect(() => {
		if (!checkAccess || access) return
		let active = true
		loadNavigationAccess().then(data => {
			if (active) setAccess(data)
		})
		return () => { active = false }
	}, [checkAccess, access])

	if (!login) {
		localStorage.removeItem('login')
		localStorage.removeItem('role')
		localStorage.removeItem('token')
		return <Navigate to="/auth/login" />;
	}

	if (checkAccess) {
		// Пока доступ не пришёл, страницу не рисуем: она успела бы запросить данные без права
		if (!access) return null

		if (!isPathAllowed(location.pathname, access)) {
			return <Navigate to={allowedFallbackPath(getDefaultHomePath(role), access)} replace />;
		}
	}

	return <Outlet context={outletContextFromParent} />;
};

function ProtectedDeliveryRoute() {
	const login = localStorage.getItem("login");
	const role = localStorage.getItem("role");
	const outletContextFromParent = useOutletContext();

	if (!login) {
		localStorage.removeItem('login')
		localStorage.removeItem('role')
		localStorage.removeItem('token')
		return <Navigate to="/auth/login" />;
	}

	// Раздел доставки доступен только колл-центру — остальных уводим на их домашнюю страницу.
	if (role !== 'ROLE_CALL_CENTER') {
		return <Navigate to={getDefaultHomePath(role)} replace />;
	}

	return <Outlet context={outletContextFromParent} />;
};

function ProtectedAdminRoute() {
	const login = localStorage.getItem("login");
	const role = localStorage.getItem("role");
	const location = useLocation();

	// Раньше здесь стояло `login === 'admin'`: администратор был ровно один.
	// Теперь их несколько, отличаются они правами, а не логином.
	if (!login || role !== 'ROLE_ADMIN') {
		localStorage.removeItem('login')
		localStorage.removeItem('role')
		localStorage.removeItem('token')
		return <Navigate to="/auth/login" />;
	}

	// Прямая ссылка в закрытый раздел уводит на доступный: сервер такой запрос всё
	// равно не выполнит, а пустая страница с ошибкой выглядит как поломка.
	const permission = adminPermissionByPath(location.pathname)

	if (permission && !hasPermission(permission)) {
		const fallback = firstAdminPath()

		// Доступных разделов нет вовсе — либо права не выданы, либо в браузере остался
		// профиль, сохранённый до появления прав админки. И то, и другое лечится
		// повторным входом, а не пустой страницей.
		if (!fallback) {
			localStorage.removeItem('login')
			localStorage.removeItem('role')
			localStorage.removeItem('token')

			return <Navigate to="/auth/login" replace />;
		}

		return <Navigate to={fallback} replace />;
	}

	return <Outlet />;
};

/**
 * Маршруты создаются один раз — роутер не пересоздаётся при смене role в Redux.
 */
export function createAppRouteElements() {
	return (
		<>
			{/* Удаление аккаунта гостя для Google Play — открыто всем, вошёл человек в кабинет или нет */}
			<Route
				path="/delete-account/:uuid"
				element={<Suspense fallback={null}><AccountDeletion /></Suspense>}
			/>

			<Route element={<GuestOnlyRoute />}>
				<Route element={<NoAuthLayout />}>
					{createPublicRoutes()}
				</Route>
			</Route>

			<Route element={<Layout />}>
				{createPrivateRoutes()}
			</Route>

			<Route element={<Layout />}>
				<Route path="*" element={<AuthenticatedFallbackRedirect />} />
			</Route>

			<Route path="/admin" element={<LayoutAdmin />}>
				<Route path="/admin" element={<AdminIndexRedirect />} />
				{createAdminPrivateRoutes()}
			</Route>

			<Route path="/delivery" element={<LayoutDelivery />}>
				{createDeliveryPrivateRoutes()}
			</Route>

			<Route path="/partner" element={<LayoutPartner />}>
				{createPartnerPrivateRoutes()}
			</Route>
		</>
	);
}

export default createAppRouteElements;
